import { ref, onUnmounted } from 'vue'
import { useAuthStore } from '@/features/auth/stores/auth.store'

// What counts as someone using the app. Vue 2 restarted its countdown on every page load,
// search, export and add-to-list; on the SPA those happen on one screen, so any click, key
// press, scroll or touch restarts it instead.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

// Restarting on every event would reset the timers constantly while someone types or scrolls.
// Once every 30 seconds is plenty against a limit measured in hours.
const ACTIVITY_THROTTLE_MS = 30_000

/**
 * Signs the user out after `timeoutMinutes` without activity, like the classic (Vue 2) app
 * (session-manager.js), with a warning 30 seconds before. The countdown restarts on activity, on
 * a change of screen (the layout calls `reschedule`) and when the tab comes back into view; a
 * hidden tab never signs anyone out.
 */
export function useSessionManager(timeoutMinutes: number = 720) {
  const auth = useAuthStore()

  const isSessionExpiring = ref(false)
  const secondsPopupIsVisible = 30
  const secondsBuffer = 20

  let sessionExpiringTimeout: ReturnType<typeof setTimeout> | null = null
  let endSessionTimeout: ReturnType<typeof setTimeout> | null = null
  let renewInFlight = false
  let lastRestartAt = 0

  // Vue 2 (session-manager.js:7-15) suppressed the expiry popup while a live search
  // deep-link was active so it never interrupted an in-flight search. On the SPA the
  // live params are `q` (top-bar search) and `runICP` (ICP run); `savedsearch` only
  // appears on legacy /V2 links, so it is not checked here.
  function hasActiveSearchDeepLink(): boolean {
    const params = new URLSearchParams(window.location.search)
    return params.has('q') || params.has('runICP')
  }

  function setupSessionExpiration() {
    isSessionExpiring.value = false
    lastRestartAt = Date.now()

    if (sessionExpiringTimeout) clearTimeout(sessionExpiringTimeout)
    if (endSessionTimeout) clearTimeout(endSessionTimeout)

    if (hasActiveSearchDeepLink()) return

    const secondsBeforePopup = timeoutMinutes * 60 - secondsPopupIsVisible - secondsBuffer
    if (secondsBeforePopup > 0) {
      sessionExpiringTimeout = setTimeout(showExpiringPopup, secondsBeforePopup * 1000)
    }
  }

  // Once the warning shows, only its button keeps the session, as in Vue 2.
  function onActivity() {
    if (isSessionExpiring.value || Date.now() - lastRestartAt < ACTIVITY_THROTTLE_MS) return
    setupSessionExpiration()
  }

  function showExpiringPopup() {
    if (document.hidden) return
    isSessionExpiring.value = true
    if (endSessionTimeout) clearTimeout(endSessionTimeout)
    endSessionTimeout = setTimeout(endSession, secondsPopupIsVisible * 1000)
  }

  async function endSession() {
    if (document.hidden || !isSessionExpiring.value || renewInFlight) return
    await auth.leaveSession()
  }

  async function renewSession() {
    if (renewInFlight) return
    renewInFlight = true
    try {
      if (sessionExpiringTimeout) clearTimeout(sessionExpiringTimeout)
      if (endSessionTimeout) clearTimeout(endSessionTimeout)
      const valid = await auth.renewSession()
      if (valid) {
        isSessionExpiring.value = false
        setupSessionExpiration()
      } else {
        await auth.leaveSession()
      }
    } finally {
      renewInFlight = false
    }
  }

  function onVisibilityChange() {
    if (!document.hidden && auth.isAuthenticated) {
      renewSession()
    }
  }

  function start() {
    setupSessionExpiration()
    document.addEventListener('visibilitychange', onVisibilityChange)
    for (const type of ACTIVITY_EVENTS) {
      document.addEventListener(type, onActivity, { capture: true, passive: true })
    }
  }

  function stop() {
    if (sessionExpiringTimeout) clearTimeout(sessionExpiringTimeout)
    if (endSessionTimeout) clearTimeout(endSessionTimeout)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    for (const type of ACTIVITY_EVENTS) {
      document.removeEventListener(type, onActivity, { capture: true })
    }
  }

  onUnmounted(() => {
    stop()
  })

  return {
    isSessionExpiring,
    start,
    stop,
    renewSession,
    // Re-evaluate scheduling/suppression against the current URL. The layout wires this
    // to route changes so a client-side nav into (or out of) a search deep-link after
    // mount is re-assessed (Vue 2 re-ran this on every full page load).
    reschedule: setupSessionExpiration,
  }
}
